import { get, post, del } from './client'

// ── Types ─────────────────────────────────────────────────────────────────────

export type AdSpendMode = 'dt_mo' | 'monthly_budget' | 'daily_manual'

export type AdSpendConfig = {
  mode:           AdSpendMode
  monthlyBudget?: number
}

// One submission covering a date range, spread evenly across its days.
export type PeriodSpendBody = {
  from:        string   // 'YYYY-MM-DD'
  to:          string   // 'YYYY-MM-DD'
  allocations: { channelId: string; amount: number }[]
  notes?:      string
  actor?:      string
}

export type PeriodSpendResult = {
  batchId: string
  from:    string
  to:      string
  days:    number
  total:   number
}

export type AdSpendEntry = {
  id:        string
  date:      string
  total:     number
  notes?:    string
  createdAt: string

  // Canonical per-channel store, keyed by MarketingChannel.id (§9.6).
  channels?: Record<string, number>

  // Pre-§9.6 fixed fields, still present on older entries.
  facebook?:  number
  instagram?: number
  tiktok?:    number
  snapchat?:  number
  google?:    number
  other?:     number

  // Set on entries created from a period submission; all days share a batchId.
  batchId?:    string
  periodFrom?: string
  periodTo?:   string
  enteredBy?:  string
}

export type AdSpendEntriesResponse = {
  month:   string
  entries: AdSpendEntry[]
  total:   number
}

export type AdSpendSource = {
  mode:    AdSpendMode
  active:  boolean
  warning: string | null
}

// Which clock financial reports lead with. Both lenses are always computed —
// see docs/cleanup-architecture.md §9.2.
export type RevenueRecognition = 'created' | 'collected'

export type FinanceSettings = {
  cogsDefaultPct:     number
  returnCostPct:      number
  carrierFeesPct:     number
  settlementLagDays:  number
  shippingDefaultPct: number
  revenueRecognition: RevenueRecognition
}

// ── Dual-lens metadata ────────────────────────────────────────────────────────

export type Lens = 'performance' | 'cash'

// How much of a performance period has finished resolving. Figures from a period
// below MATURITY_FINAL_THRESHOLD are still moving and must not be read as final.
export type PeriodMaturity = {
  month:          string
  totalOrders:    number
  resolvedOrders: number
  pendingOrders:  number
  maturityPct:    number   // 0–100
  isFinal:        boolean
}

export type CohortSlice = {
  cohortMonth: string
  label:       string
  amount:      number
  orderCount:  number
  pct:         number      // 0–100
}

// Cash that arrived this month, split by the period whose work produced it.
export type SettlementBridge = {
  month:           string
  totalCollected:  number
  fromThisMonth:   number
  fromPriorMonths: number
  thisMonthPct:    number  // 0–100
  cohorts:         CohortSlice[]
}

export type FinanceDashboard = {
  month:   string
  revenue: {
    gross:        number
    returns:      number
    net:          number
    ordersCount:  number
    returnsCount: number
  }
  cogs: {
    total:         number
    knownFromData: number
    estimated:     number
    knownCount:    number
    missingCount:  number
    defaultPct:    number
  }
  shipping: {
    estimated: number
    actual:    number | null
    used:      number
    variance:  number | null
  }
  settlements: {
    settledCount:  number
    pendingCount:  number
    settledAmount: number
    pendingAmount: number
  }
  marketing: {
    spend:         number
    mode:          AdSpendMode
    monthlyBudget: number | null
    entriesCount:  number
    budgetUsedPct: number | null
    adSpendSource: AdSpendSource
  }
  cod: {
    totalExpected:  number
    carrierFees:    number
    carrierFeesPct: number
  }
  returns: {
    count:        number
    revenue:      number
    handlingCost: number
    handlingPct:  number
  }
  profit: {
    gross:              number
    net:                number
    grossMargin:        number
    netMargin:          number
    contributionMargin: number | null
    // `net` is operating profit (costs that scale with orders). `netAfterFixed`
    // subtracts fixed monthly expenses and is the real bottom line — see §9.4.
    netAfterFixed:       number
    netAfterFixedMargin: number
  }
  delivery: {
    deliveredCount: number
    totalOrders:    number
    deliveryRate:   number   // 0–100
  }
  gateway:  { fees: number }   // payment gateway commission, actual (§9.8)
  lens:     Lens            // always 'performance' — grouped by created_at
  maturity: PeriodMaturity
  fixedExpenses: { total: number; itemCount: number }
  orderTypes:    OrderTypeBreakdown
}

// ── Per-order-type funnel + cost (§9.7) ───────────────────────────────────────

export type OrderTypeKey = 'normal' | 'exchange' | 'return'

// Costs exist only for orders whose settlement has been uploaded, so the average
// divides by costedCount rather than by the whole population.
export type LegCost = {
  count:       number
  costedCount: number
  totalCost:   number
  avgCost:     number
}

export type OrderTypeMetric = {
  orderType:     OrderTypeKey
  total:         number
  sharePct:      number
  confirmed:     number
  confirmedPct:  number
  // Pre-shipping confirmation, after FIFO reserved the stock. The gap from
  // `confirmed` is a customer who backed out once goods were committed.
  shipConfirmed:    number
  shipConfirmedPct: number
  dispatched:    number
  dispatchedPct: number
  succeeded:     LegCost
  failed:        LegCost
  successRate:   number   // succeeded ÷ dispatched
  failureRate:   number   // failed    ÷ dispatched
  // Maturity for this type alone, so a card showing one type is not qualified by
  // a percentage describing a different population.
  resolved:      number
  maturityPct:   number
  isFinal:       boolean
  totalShippingCost: number
}

export type OrderTypeBreakdown = {
  month:           string
  totalOrders:     number
  types:           OrderTypeMetric[]
  costCoveragePct: number
}

// ── Fixed expenses (§9.4) ─────────────────────────────────────────────────────

export type FixedExpenseItem = {
  id:        string
  name:      string
  category?: string
  amount:    number
  recurring: boolean
  createdAt: string
  updatedAt: string
}

export type FixedExpenseChangeType =
  | 'created' | 'amount_changed' | 'renamed' | 'deleted' | 'carried_forward'

export type FixedExpenseHistoryEntry = {
  id:             string
  itemId:         string
  itemName:       string
  changeType:     FixedExpenseChangeType
  previousAmount: number | null
  newAmount:      number | null
  note:           string
  actor:          string
  at:             string
}

export type FixedExpensesResponse = {
  month:          string
  items:          FixedExpenseItem[]
  total:          number
  carriedForward: number   // how many items were auto-copied when the month opened
  history:        FixedExpenseHistoryEntry[]
}

export type FixedExpenseUpsert = {
  id?:       string
  name:      string
  category?: string
  amount:    number
  recurring: boolean
  note?:     string   // required by the API when changing an existing amount
  actor?:    string
}

export type SettlementRecord = {
  orderName:      string
  createdAt:      string
  netRevenue:     number
  totalCost:      number
  actualShipping: number
  codAmount:      number
  deliveryStatus: string
  settlementDate: string
  codCollected:   number
  cashoutId:      string
}

export type SettlementsResponse = {
  month:             string
  count:             number
  totalCodCollected: number
  totalNetRevenue:   number
  settlements:       SettlementRecord[]
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function qs(shop: string, params: Record<string, string> = {}): string {
  return `?${new URLSearchParams({ shop, ...params }).toString()}`
}

// ── API ───────────────────────────────────────────────────────────────────────

// ── Cash Position ─────────────────────────────────────────────────────────────

export type CashBucket = {
  label:      string
  minDays:    number
  maxDays:    number | null
  orderCount: number
  codAtRisk:  number
}

export type CashPosition = {
  unsettledOrders:       number
  totalCodAtRisk:        number
  oldestUnsettledDays:   number
  buckets:               CashBucket[]
  avgDailySettlement:    number
  avgSettlementLagDays:  number | null
  settlementLagSampleSize: number
  collectedThisMonth:    number
  pendingThisMonth:      number
  confidence: {
    score:       number   // 0–1 fraction — multiply by 100 to display
    level:       'high' | 'medium' | 'low'
    freshness:   'live' | 'stale' | 'estimated'
    reasons:     string[]
    missingData: string[]
  }
  // Prepaid orders awaiting a gateway payout — separate from outstanding COD,
  // since nothing about delivery moves this money (§9.8).
  gatewayPendingOrders:     number
  gatewayPendingAmount:     number
  oldestGatewayPendingDays: number
  lens:   Lens              // always 'cash' — grouped by settlement_date
  bridge: SettlementBridge
}

export const fcApi = {
  // Settings
  getSettings: (shop: string): Promise<FinanceSettings> =>
    get(`/finance/settings${qs(shop)}`),

  saveSettings: (shop: string, settings: Partial<FinanceSettings>): Promise<FinanceSettings> =>
    post(`/finance/settings${qs(shop)}`, settings),

  // Ad Spend Config
  getAdSpendConfig: (shop: string): Promise<AdSpendConfig> =>
    get(`/finance/ad-spend/config${qs(shop)}`),

  saveAdSpendConfig: (shop: string, config: Partial<AdSpendConfig>): Promise<AdSpendConfig> =>
    post(`/finance/ad-spend/config${qs(shop)}`, config),

  // Ad Spend — period entry (§9.6)
  saveAdSpendPeriod: (shop: string, body: PeriodSpendBody): Promise<PeriodSpendResult> =>
    post(`/finance/ad-spend/period${qs(shop)}`, body),

  deleteAdSpendPeriod: (shop: string, month: string, batchId: string): Promise<{ ok: boolean; removed: number }> =>
    del(`/finance/ad-spend/period/${encodeURIComponent(batchId)}${qs(shop, { month })}`),

  // Ad Spend Entries
  getAdSpendEntries: (shop: string, month?: string): Promise<AdSpendEntriesResponse> => {
    const params: Record<string, string> = {}
    if (month) params.month = month
    return get(`/finance/ad-spend/entries${qs(shop, params)}`)
  },

  addAdSpendEntry: (shop: string, entry: Omit<AdSpendEntry, 'id' | 'total' | 'createdAt'>): Promise<AdSpendEntry> =>
    post(`/finance/ad-spend/entries${qs(shop)}`, entry),

  deleteAdSpendEntry: (shop: string, entryId: string, month: string): Promise<{ ok: boolean }> =>
    del(`/finance/ad-spend/entries/${encodeURIComponent(entryId)}${qs(shop, { month })}`),

  // Dashboard
  getDashboard: (shop: string, month?: string): Promise<FinanceDashboard> => {
    const params: Record<string, string> = {}
    if (month) params.month = month
    return get(`/finance/dashboard${qs(shop, params)}`)
  },

  // Settlements
  getSettlements: (shop: string, month?: string): Promise<SettlementsResponse> => {
    const params: Record<string, string> = {}
    if (month) params.month = month
    return get(`/finance/settlements${qs(shop, params)}`)
  },

  // Cash position
  getCashPosition: (shop: string): Promise<CashPosition> =>
    get(`/finance/cash${qs(shop)}`),

  // Fixed expenses
  getFixedExpenses: (shop: string, month?: string): Promise<FixedExpensesResponse> => {
    const params: Record<string, string> = {}
    if (month) params['month'] = month
    return get(`/finance/fixed-expenses${qs(shop, params)}`)
  },

  saveFixedExpense: (shop: string, month: string, item: FixedExpenseUpsert): Promise<{ item: FixedExpenseItem; total: number }> =>
    post(`/finance/fixed-expenses${qs(shop, { month })}`, item),

  deleteFixedExpense: (shop: string, month: string, id: string, actor: string, note?: string): Promise<{ ok: boolean; total: number }> => {
    const params: Record<string, string> = { month, actor }
    if (note) params['note'] = note
    return del(`/finance/fixed-expenses/${encodeURIComponent(id)}${qs(shop, params)}`)
  },

  // Governorate breakdown
  getGovernorateBreakdown: (shop: string, month?: string): Promise<GovernorateBreakdown> => {
    const params: Record<string, string> = {}
    if (month) params['month'] = month
    return get(`/finance/breakdown/governorate${qs(shop, params)}`)
  },
}

export type GovernorateMetric = {
  governorate:    string
  orderCount:     number
  revenue:        number
  cogs:           number
  grossProfit:    number
  grossMargin:    number
  deliveredCount: number
  returnCount:    number
  deliveryRate:   number
}

export type GovernorateBreakdown = {
  month:         string
  totalOrders:   number
  mappedOrders:  number
  unknownOrders: number
  governorates:  GovernorateMetric[]
  confidence:    { level: 'high' | 'medium' | 'low'; score: number; freshness: string; reasons: string[]; missingData: string[] }
}

// ── Accounting (settlement upload + unmatched import) ─────────────────────────

export type SettlementUploadRow = {
  bostaId:        string
  orderRef:       string
  settlementDate: string
  shippingFee:    number
  codCollected:   number
  cashoutId:      string
}

export type UnmatchedExportRow = {
  bostaId:        string
  orderRef:       string
  settlementDate: string
  shippingFee:    number
  codCollected:   number
  cashoutId:      string
  segment:        string
}

export type SettlementUploadResult = {
  matched:        number
  unmatched:      number
  totalFees:      number
  totalCollected: number
  errors:         string[]
  unmatchedData:  UnmatchedExportRow[]
}

export type ShippingSlice = {
  count:   number
  total:   number
  average: number
}

export type ShippingTypeBreakdown = {
  delivered:            ShippingSlice
  returned_to_business: ShippingSlice
  failureRate:          number
}

export type ShippingCostsBreakdown = {
  normal:   ShippingTypeBreakdown
  exchange: ShippingTypeBreakdown
  return:   ShippingTypeBreakdown
}

// Payment gateway payout row, already column-mapped by the accountant (§9.8).
export type GatewaySettlementRow = {
  orderRef:   string
  netAmount:  number
  fee:        number
  payoutDate: string
  reference?: string
}

export type GatewaySettlementResult = {
  gateway:       string
  matched:       number
  unmatched:     number
  totalNet:      number
  totalFees:     number
  unmatchedRows: GatewaySettlementRow[]
  errors:        string[]
}

export const accountingApi = {
  uploadGatewaySettlement: (shop: string, rows: GatewaySettlementRow[], gateway: string): Promise<GatewaySettlementResult> =>
    post(`/accounting/gateway-settlement?shop=${encodeURIComponent(shop)}`, { rows, gateway }, { timeout: 60_000 }),

  uploadSettlement: (shop: string, rows: SettlementUploadRow[]): Promise<SettlementUploadResult> =>
    post(`/accounting/settlement?shop=${encodeURIComponent(shop)}`, { rows }, { timeout: 60_000 }),

  getShippingBreakdown: (shop: string): Promise<ShippingCostsBreakdown> =>
    get(`/accounting/shipping-costs-breakdown?shop=${encodeURIComponent(shop)}`),
}
